import { __ } from "@wordpress/i18n";

/**
 * Read the product id out of an element's Interactivity API context.
 *
 * `data-wp-context` is not plain JSON: a store that names its namespace emits
 * `namespace::{…}`, which is what the product loop does
 * (`woocommerce/products::{"productId":60,…}`). Parsing the attribute whole
 * therefore threw on every card in a loop, and the id fell through to 0 — the
 * value the endpoint reads as "no product", so Quick View opened empty.
 *
 * @param {Element} element Element carrying the attribute.
 * @return {number} Product id, or 0 when the attribute is absent or unusable.
 */
const readProductIdFromContext = (element) => {
  const raw = element && element.getAttribute("data-wp-context");
  if (!raw) {
    return 0;
  }

  const separator = raw.indexOf("::");
  const json = separator === -1 ? raw : raw.slice(separator + 2);

  try {
    const context = JSON.parse(json);
    return parseInt(context.productId, 10) || 0;
  } catch (error) {
    return 0;
  }
};

document.addEventListener("DOMContentLoaded", () => {
  const quickViewProductBlocks = document.querySelectorAll(
    ".brandy-quick-view-product-block"
  );

  // Define modal functions outside the loop so they can be reused
  const closeModal = (backdrop) => {
    // Remove overflow hidden from the body
    window.jQuery(document.body).css("overflow", "");
    if (backdrop) {
      backdrop.remove();
    }
  };

  const openModal = (e, block) => {
    e.preventDefault();

    // Add loading state to the button
    const button = e.target;
    // Get current product ID from the Product Template context
    let currentProductId = 0;

    const productCard = button.closest(
      ".wc-block-grid__product, .product, .woocommerce-loop-product__link, .wp-block-woocommerce-product-summary, .wp-block-woocommerce-product-image, .wp-block-woocommerce-product-title, .wp-block-woocommerce-product-template-item, li[data-wp-context*='productId']"
    );

    if (productCard) {
      // Try multiple ways to get the product ID from the current product card
      let productId =
        productCard.getAttribute("data-product-id") ||
        productCard.getAttribute("data-id") ||
        productCard.dataset.productId ||
        productCard.getAttribute("data-wc-product-id");

      // Special handling for WooCommerce product context
      if (!productId) {
        const contextId = readProductIdFromContext(productCard);
        if (contextId) {
          productId = contextId.toString();
        }
      }

      if (productId) {
        currentProductId = parseInt(productId);
      }

      // If still no ID, try to find it in child elements
      if (!currentProductId) {
        const productIdElement = productCard.querySelector(
          "[data-product-id], [data-id], [data-wc-product-id]"
        );
        if (productIdElement) {
          const id =
            productIdElement.getAttribute("data-product-id") ||
            productIdElement.getAttribute("data-id") ||
            productIdElement.getAttribute("data-wc-product-id") ||
            productIdElement.dataset.productId;
          if (id) {
            currentProductId = parseInt(id);
          }
        }
      }
    }

    // If still no product ID, try to get from the block's parent elements
    if (!currentProductId && block) {
      const blockParent = block.closest(
        ".wc-block-grid__product, .product, .woocommerce-loop-product__link, .wp-block-woocommerce-product-summary, .wp-block-woocommerce-product-image, .wp-block-woocommerce-product-title, li[data-wp-context*='productId']"
      );

      if (blockParent) {
        let productId =
          blockParent.getAttribute("data-product-id") ||
          blockParent.getAttribute("data-id") ||
          blockParent.dataset.productId ||
          blockParent.getAttribute("data-wc-product-id");

        // Special handling for WooCommerce product context
        if (!productId) {
          const contextId = readProductIdFromContext(blockParent);
          if (contextId) {
            productId = contextId.toString();
          }
        }

        if (productId) {
          currentProductId = parseInt(productId);
        }
      }
    }

    // Last resort: try to get from the product template context
    if (!currentProductId && block) {
      const productTemplate = block.closest(
        ".wp-block-woocommerce-product-template"
      );
      if (productTemplate) {
        // Get product ID from the product template context
        const productIdElement =
          productTemplate.querySelector("[data-product-id]");
        if (productIdElement) {
          currentProductId = parseInt(
            productIdElement.getAttribute("data-product-id")
          );
        }

        // Alternative: get from WooCommerce product data
        const wcProductData = productTemplate.querySelector(
          ".wc-block-grid__product"
        );
        if (wcProductData && wcProductData.dataset.productId) {
          currentProductId = parseInt(wcProductData.dataset.productId);
        }
      }
    }

    // If still no product ID, try alternative methods
    if (!currentProductId) {
      // Try to find product ID from any link in the product card
      if (productCard) {
        const productLink = productCard.querySelector("a[href*='product']");
        if (productLink) {
          const href = productLink.getAttribute("href");
          const productIdMatch = href.match(/product\/([^\/\?]+)/);
          if (productIdMatch) {
            currentProductId = parseInt(productIdMatch[1]);
          }
        }
      }

      // Try to find product ID from the page URL if we're on a product page
      if (!currentProductId && window.location.href.includes("product")) {
        const urlMatch = window.location.href.match(/product\/([^\/\?]+)/);
        if (urlMatch) {
          currentProductId = parseInt(urlMatch[1]);
        }
      }
    }

    // If still no product ID found, we'll send 0 to get the default template
    if (!currentProductId) {
      currentProductId = 0;
    }

    const ajaxUrl = window.brandyBlocks.ajax.path;

    // Show loading state with skeleton loader
    const modalContent = document.querySelector(
      ".brandy-quick-view-modal-content"
    );
    if (modalContent) {
      modalContent.innerHTML = `
        <div class="brandy-quick-view-skeleton">
          <div class="skeleton-image"></div>
          <div class="skeleton-content">
            <div class="skeleton-title"></div>
            <div class="skeleton-price"></div>
            <div class="skeleton-description"></div>
            <div class="skeleton-description"></div>
            <div class="skeleton-description"></div>
            <div class="skeleton-button"></div>
          </div>
        </div>
      `;
    }

    fetch(`${ajaxUrl}?action=quick_view_product&product_id=${currentProductId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          if (modalContent) {
            // Handle both old format (direct content) and new format (object with content and scripts)
            let content = data.data;

            if (
              typeof data.data === "object" &&
              data.data !== null &&
              data.data.content
            ) {
              content = data.data.content;
            }

            // Set the HTML content
            modalContent.innerHTML = content;

            // Reinitialize WooCommerce functionality for the popup content
            setTimeout(() => {
              const $ = window.jQuery;
              if ($ && typeof wc_single_product_params !== "undefined") {
                // Initialize product gallery
                $(modalContent)
                  .find(".woocommerce-product-gallery")
                  .each(function () {
                    $(this).trigger("wc-product-gallery-before-init", [
                      this,
                      wc_single_product_params,
                    ]);
                    $(this).wc_product_gallery(wc_single_product_params);
                    $(this).trigger("wc-product-gallery-after-init", [
                      this,
                      wc_single_product_params,
                    ]);
                  });

                // Initialize variation forms
                $(modalContent)
                  .find(".variations_form")
                  .each(function () {
                    $(this).wc_variation_form();
                  });

                // Initialize tabs
                $(modalContent)
                  .find(".wc-tabs-wrapper, .woocommerce-tabs")
                  .trigger("init");

                // Initialize quantity inputs
                $(modalContent)
                  .find(".quantity .qty")
                  .on("change input", function () {
                    const $qty = $(this);
                    const val = parseFloat($qty.val());
                    const max = parseFloat($qty.attr("max"));
                    const min = parseFloat($qty.attr("min"));
                    const step = parseFloat($qty.attr("step"));

                    if (max && val > max) {
                      $qty.val(max);
                    }
                    if (min && val < min) {
                      $qty.val(min);
                    }
                    if (step) {
                      const remainder = val % step;
                      if (remainder !== 0) {
                        $qty.val(val - remainder + step);
                      }
                    }
                  });

                // Initialize zoom if enabled
                if (wc_single_product_params.zoom_enabled) {
                  $(modalContent)
                    .find(".woocommerce-product-gallery__image img")
                    .each(function () {
                      if (
                        $(this)
                          .parent()
                          .hasClass("woocommerce-product-gallery__image")
                      ) {
                        $(this)
                          .parent()
                          .zoom(wc_single_product_params.zoom_options);
                      }
                    });
                }

                // Initialize flexslider if enabled
                if (wc_single_product_params.flexslider_enabled) {
                  $(modalContent)
                    .find(".woocommerce-product-gallery")
                    .flexslider(wc_single_product_params.flexslider);
                }

                // Trigger WooCommerce events
                $(document.body).trigger("wc_fragments_loaded");
                $(document.body).trigger("wc_cart_button_updated");
              }
            }, 100);

            // Add fade-in effect for loaded content
            modalContent.style.opacity = "0";
            modalContent.style.transition = "opacity 0.3s ease-in-out";
            setTimeout(() => {
              modalContent.style.opacity = "1";
            }, 10);

            // Trigger a custom event to notify that quick view content is loaded
            const quickViewEvent = new CustomEvent("brandyQuickViewLoaded", {
              detail: {
                productId: currentProductId,
                modalContent: modalContent,
              },
            });
            document.dispatchEvent(quickViewEvent);
          }
        } else {
          console.error("Error loading product:", data.data);
          if (modalContent) {
            modalContent.innerHTML = `<div class="brandy-quick-view-error"><p>Error: ${data.data}</p></div>`;
          }
        }
      })
      .catch((error) => {
        console.error("AJAX Error:", error);
        if (modalContent) {
          modalContent.innerHTML = `<div class="brandy-quick-view-error"><p>Error loading product. Please try again.</p><p class="error-details">${error.message}</p></div>`;
        }
      });

    // Add overflow hidden to the body to prevent scrolling
    window.jQuery(document.body).css("overflow", "hidden");
  };

  const createBackdrop = (block) => {
    const backdropColor = block.getAttribute("data-backdrop-color");
    const backgroundColor = block.getAttribute("data-background-color");
    const borderWidth = block.getAttribute("data-border-width");
    const borderStyle = block.getAttribute("data-border-style");
    const borderColor = block.getAttribute("data-border-color");
    const borderRadius = block.getAttribute("data-border-radius");
    const paddingTop = block.getAttribute("data-padding-top");
    const paddingRight = block.getAttribute("data-padding-right");
    const paddingBottom = block.getAttribute("data-padding-bottom");
    const paddingLeft = block.getAttribute("data-padding-left");
    const boxShadow = block.getAttribute("data-box-shadow");
    // In frontend, create element with the class "quick-view-product-backdrop"
    const backdrop = document.createElement("div");
    backdrop.className = "brandy-quick-view-product-backdrop";
    backdrop.style.backgroundColor = backdropColor;
    // Add new element to backdrop with the class "quick-view-product-block"
    const quickViewProductBlock = document.createElement("div");
    quickViewProductBlock.className = "brandy-quick-view-product-wrapper";
    // Add new element to quickViewProductBlock with the class "quick-view-product-content"
    const quickViewProductContent = document.createElement("div");
    quickViewProductContent.className = "brandy-quick-view-product-content";

    quickViewProductContent.style.backgroundColor =
      backgroundColor || "var(--wp--custom--background)";
    quickViewProductContent.style.padding = paddingTop || "20px";
    quickViewProductContent.style.paddingRight = paddingRight || "20px";
    quickViewProductContent.style.paddingBottom = paddingBottom || "20px";
    quickViewProductContent.style.paddingLeft = paddingLeft || "20px";
    quickViewProductContent.style.border = borderWidth || "0px";
    quickViewProductContent.style.borderStyle = borderStyle || "solid";
    quickViewProductContent.style.borderColor = borderColor || "#000000";
    quickViewProductContent.style.borderRadius = borderRadius || "10px";
    quickViewProductContent.style.boxShadow = boxShadow || "";
    // Add new element to quickViewProductContent with the class "quick-view-product-inner-content"
    const quickViewProductInnerContent = document.createElement("div");
    quickViewProductInnerContent.className =
      "brandy-quick-view-product-inner-content";
    // Add new element to quickViewProductInnerContent with the class "quick-view-modal-content"
    const quickViewProductModalContent = document.createElement("div");
    quickViewProductModalContent.className = "brandy-quick-view-modal-content";
    quickViewProductInnerContent.appendChild(quickViewProductModalContent);
    quickViewProductContent.appendChild(quickViewProductInnerContent);
    quickViewProductBlock.appendChild(quickViewProductContent);
    backdrop.appendChild(quickViewProductBlock);
    backdrop.addEventListener("click", (e) => {
      if (e.target.classList.contains("brandy-quick-view-product-backdrop")) {
        closeModal(backdrop);
      }
    });
    document.body.appendChild(backdrop);
  };

  quickViewProductBlocks.forEach((block) => {
    if (
      block.parentElement.classList.contains("block-editor-block-list__layout")
    ) {
      return;
    }
    const buttons = block
      ? block.querySelectorAll(".brandy-button, button, .wp-block-button__link")
      : [];

    // Add click listeners to all buttons in the button area
    buttons.forEach((button) => {
      button.addEventListener("click", (e) => {
        createBackdrop(block);
        openModal(e, block);
      });
    });

    // Add escape key listener for closing modal
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        const backdrop = document.querySelector(
          ".brandy-quick-view-product-backdrop"
        );
        if (backdrop) {
          closeModal(backdrop);
        }
      }
    });
  });
});
